import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../../components/Layout';
import { Ic } from '../../design/icons';
import { Avatar, Chip } from '../../design/primitives';

const API = `${window.location.origin}/api`;

const ACTION_LABELS = {
  'super_admin.user_update': 'Kullanıcı Güncelleme',
  'super_admin.user_promote': 'Süper Admin Yapma',
  'super_admin.user_demote': 'Süper Admin Düşürme',
  'super_admin.workspace_update': 'Workspace Güncelleme',
  'super_admin.campaign_send': 'Kampanya Gönderimi',
};

const TARGET_LABELS = {
  user: 'Kullanıcı',
  workspace: 'Workspace',
  campaign: 'Kampanya',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'Az önce';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} gün önce`;
  return new Date(iso).toLocaleDateString('tr-TR');
}

export default function SuperAdminAuditLog() {
  usePageHeader([{ label: 'Süper Admin' }, { label: 'Süper Admin Günlüğü' }], null, []);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterTarget, setFilterTarget] = useState('all');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debounced, filterAction, filterTarget]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (debounced) params.append('search', debounced);
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterTarget !== 'all') params.append('target_type', filterTarget);
      const res = await axios.get(`${API}/super-admin/audit-log?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Günlük yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, debounced, filterAction, filterTarget]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filtersActive = debounced || filterAction !== 'all' || filterTarget !== 'all';

  const todayCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return logs.filter((l) => (l.createdAt || '').slice(0, 10) === today).length;
  }, [logs]);

  const distinctActors = useMemo(() => {
    return new Set(logs.map((l) => l.userId).filter(Boolean)).size;
  }, [logs]);

  return (
    <div className="ox-page space-y-4" style={{ padding: 24 }}>
      <div className="ox-grid-stats-6">
        <StatTile label="Toplam (filtreli)" value={total} />
        <StatTile label="Bu sayfada" value={logs.length} />
        <StatTile label="Bugün" value={todayCount} color="var(--accent)" />
        <StatTile label="Aktör Sayısı" value={distinctActors} />
        <StatTile label="Sayfa" value={`${page} / ${totalPages || 1}`} small />
        <StatTile label="Olay Tipleri" value={new Set(logs.map((l) => l.action)).size} small />
      </div>

      <div className="ox-surface rounded-md p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[260px] sm:flex-none basis-full sm:basis-auto">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.search}</span>
          <input
            className="ox-input pl-8"
            placeholder="Detay, aktör veya aksiyon ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="ox-select" value={filterAction} onChange={(e) => setFilterAction(e.target.value)} style={{ maxWidth: 220 }}>
          <option value="all">Tüm Aksiyonlar</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="ox-select" value={filterTarget} onChange={(e) => setFilterTarget(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="all">Tüm Hedefler</option>
          {Object.entries(TARGET_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {filtersActive && (
          <button
            className="ox-btn ox-btn-ghost"
            onClick={() => { setSearch(''); setFilterAction('all'); setFilterTarget('all'); }}
          >
            {Ic.x} Temizle
          </button>
        )}
        <button className="ox-btn ox-btn-ghost ml-auto" onClick={fetchLogs} title="Yenile">
          {Ic.refresh}
        </button>
      </div>

      <div className="ox-surface rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center ox-text-muted">Yükleniyor...</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center ox-text-muted">Henüz süper admin işlemi kaydedilmemiş</div>
        ) : (
          <div className="ox-table-wrap ox-table-wide">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Zaman</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Aktör</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Aksiyon</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Hedef</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Detay</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden lg:table-cell">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2.5 align-top">
                      <div className="text-[12.5px]">{formatDate(l.createdAt)}</div>
                      <div className="text-[11px] ox-text-muted">{formatRelative(l.createdAt)}</div>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar u={{ id: l.userId, name: l.userName }} size={24} />
                        <div className="min-w-0">
                          <div className="text-[12.5px] truncate">{l.userName || '—'}</div>
                          <div className="text-[11px] ox-text-muted truncate">{l.userRole}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <Chip color="#b45309" bg="var(--surface-muted)">
                        {ACTION_LABELS[l.action] || l.action}
                      </Chip>
                    </td>
                    <td className="px-3 py-2.5 align-top hidden md:table-cell">
                      {l.targetType ? (
                        <div>
                          <div className="text-[12.5px]">{TARGET_LABELS[l.targetType] || l.targetType}</div>
                          <div className="text-[11px] ox-text-muted truncate" style={{ maxWidth: 160 }}>{l.targetId}</div>
                        </div>
                      ) : (
                        <span className="ox-text-dim text-[12px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="text-[12.5px]" style={{ color: 'var(--text)' }}>{l.details || '—'}</div>
                    </td>
                    <td className="px-3 py-2.5 align-top hidden lg:table-cell">
                      <span className="text-[11.5px] ox-text-muted">{l.ipAddress || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="text-[12px] ox-text-muted">
              {(page - 1) * 50 + 1}–{Math.min(page * 50, total)} / {total}
            </span>
            <div className="flex items-center gap-1">
              <button className="ox-btn ox-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Önceki</button>
              <button className="ox-btn ox-btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, color, small }) {
  return (
    <div className="ox-surface rounded-md p-3">
      <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">{label}</div>
      <div
        className="ox-tnum font-semibold mt-1"
        style={{ color: color || 'var(--text)', fontSize: small ? 16 : 22 }}
      >
        {value}
      </div>
    </div>
  );
}
